import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { fetchTrips, type DispatchTrip } from '@/features/delman/api/tripApi'
import { delmanTerms } from '@/features/delman/delmanTerms'
import { tripProgress } from '@/features/delman/tripProgress'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonList } from '@/shared/components/Skeleton'

function tripStatusBadge(status: string): string {
  if (status === 'in_progress') return 'badge-warning'
  if (status === 'completed') return 'badge-success'
  if (status === 'cancelled') return 'badge-danger'
  return 'badge-draft'
}

function tripStatusLabel(status: string): string {
  return delmanTerms.trip.status[status] ?? status
}

/** GET /warehouse/dispatch-trips?date= — daftar sesi kirim hari ini (§5 spec). */
export function DelmanTripPage() {
  const navigate = useNavigate()
  const device = getDelmanDevice()

  const [trips, setTrips] = useState<DispatchTrip[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const reload = useCallback(() => {
    let cancelled = false
    setIsLoading(true)
    setLoadError(false)
    fetchTrips()
      .then((res) => {
        if (!cancelled) setTrips(res)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => reload(), [reload, refreshKey])

  useEffect(() => {
    function onRefresh() {
      if (document.visibilityState === 'hidden') return
      setRefreshKey((k) => k + 1)
    }
    window.addEventListener('focus', onRefresh)
    document.addEventListener('visibilitychange', onRefresh)
    return () => {
      window.removeEventListener('focus', onRefresh)
      document.removeEventListener('visibilitychange', onRefresh)
    }
  }, [])

  useEffect(() => {
    const hasActive = trips.some((t) => t.status === 'dispatched' || t.status === 'in_progress')
    if (!hasActive) return undefined
    const timer = window.setInterval(() => setRefreshKey((k) => k + 1), 8_000)
    return () => window.clearInterval(timer)
  }, [trips])

  const sorted = useMemo(
    () => [...trips].sort((a, b) => (a.status === b.status ? 0 : a.status === 'completed' ? 1 : -1)),
    [trips],
  )

  return (
    <section className="space-y-3">
      <div className="card relative space-y-2">
        <button
          type="button"
          className="btn-outline absolute top-3 right-3 min-w-[44px] px-3 z-10"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={isLoading}
          aria-label={delmanTerms.trip.refreshAria}
          title={delmanTerms.trip.refreshAria}
        >
          <span className={`material-symbols-outlined ${isLoading ? 'animate-spin' : ''}`}>refresh</span>
        </button>
        <div className="pr-12">
          <p className="page-title break-words">{delmanTerms.trip.pageTitle}</p>
          <p className="text-body-sm text-on-surface-variant mt-1">
            {device ? delmanTerms.trip.subtitle(device.delmanName) : '—'}
          </p>
        </div>
      </div>

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{delmanTerms.trip.loadError}</span>
          <button type="button" className="btn-outline min-h-[36px] text-body-sm" onClick={() => setRefreshKey((k) => k + 1)}>
            {delmanTerms.trip.retry}
          </button>
        </div>
      )}

      {isLoading && <SkeletonList count={3} label={delmanTerms.trip.loading} />}

      {!isLoading && !loadError && sorted.length === 0 && (
        <EmptyState icon="local_shipping" title={delmanTerms.trip.emptyTitle} description={delmanTerms.trip.emptyDescription} />
      )}

      {!isLoading &&
        !loadError &&
        sorted.map((trip) => {
          const { done, total } = tripProgress(trip.dispatches)
          return (
          <button
            key={trip.id}
            type="button"
            className="card w-full text-left hover:border-primary transition-colors"
            onClick={() => navigate(`/delman/trip/${trip.id}`)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-body-lg font-semibold text-on-surface break-words">{trip.warehouse_name}</p>
                <p className="text-body-sm text-on-surface-variant mt-1">
                  {delmanTerms.trip.progressDapur(done, total)} · {delmanTerms.trip.dispatchCount(trip.dispatches.length)}
                </p>
                <p className="text-label-md font-mono text-on-surface-variant mt-1">{trip.trip_code}</p>
              </div>
              <span className={tripStatusBadge(trip.status)}>{tripStatusLabel(trip.status)}</span>
            </div>
          </button>
          )
        })}
    </section>
  )
}
